import type { FragmentsModel } from '@thatopen/fragments'
import { useAppStore } from '@core/store'
import type { ColorByCriterion } from '@viewer/store/viewerSlice'
import { viewerApi } from '@viewer/lib/viewerApi'
import { rawIfc } from '@viewer/lib/rawIfc'
import * as paintEngine from '@viewer/lib/paintEngine'

// ============================================================================
// Paint engine (rewritten 2026-05-10).
//
// Three colour layers, applied in order on every repaint:
//   1. Per-class palette       (when classColorsEnabled === true)
//   2. ColorBy criterion       (when colorByActive !== null in store)
//   3. Manual paints           (per-element, persisted in module map)
//
// Concurrency model:
//   * repaintModel is serialized per model; concurrent callers chain
//     onto the previous promise. Kills the race where multiple
//     onViewUpdated firings during an orbit each issued resetColor +
//     setColor and stomped on each other, leaving items uncoloured.
//   * resetColor is OPT-IN via { reset: true }. Only layer-state
//     changes (criterion change, palette toggle, manual paint added)
//     reset; tile-spawn and selection refreshes do not. Critical
//     because resetColor is what was wiping good colours during orbit.
//
// Federation:
//   * legendCache covers ALL loaded models. ensureLegend extends the
//     cache to include any newly-loaded model so a freshly-federated
//     IFC picks up the active criterion automatically.
//   * The same bucket key gets the same colour across every model.
//
// Bitmask leak (Fragments 3.4):
//   * Each unique (id, colour) tuple consumes a slot in the worker's
//     HighlightHelper bitmask. Our pipeline issues a bounded set of
//     tuples (palette + legend + manual). Re-applying the SAME tuples
//     on tile spawn doesn't grow the bitmask, so we don't need a
//     reset on every repaint, only when colours actually change.
// ============================================================================

/** Manual paints registered by paintCustomColor (Aparência panel).
 *  Layer 3 of the repaint pipeline. modelId → localId → hex. */
const manualPaints = new Map<string, Map<number, string>>()

/** Diff overlay registered by the Comparar IFC panel. Layer 4 (top):
 *  an explicit comparison mode that wins over everything else. Kept
 *  separate from manualPaints so clearing the diff never touches the
 *  user's own Aparência paints. modelId → localId → hex. */
const diffPaints = new Map<string, Map<number, string>>()

/** Cached getItemsOfCategories(all) per model. The mapping is
 *  immutable for the lifetime of a loaded FragmentsModel, so we
 *  compute it once on first repaint and reuse on tile-spawn
 *  refreshes to avoid a worker round-trip per tile. */
const itemCategoryCache = new Map<string, Record<string, number[]>>()

/** Per-model serialization queue: concurrent repaintModel calls chain
 *  onto the previous promise, eliminating the resetColor-vs-setColor
 *  race when onViewUpdated fires several times per second. */
const repaintInflight = new Map<string, Promise<void>>()

// The Fragments 3.4 setColor circuit-breaker is gone: the paintEngine
// pipeline writes per-item colours into a custom shader uniform texture
// and never calls model.setColor / resetColor, so the bitmask-overflow
// path that triggered the breaker can no longer occur.
function resetCircuitForModel(_modelId: string): void {}
function resetAllCircuits(): void {}

export function isPaintCircuitTripped(_modelId: string): boolean {
  return false
}

export function anyPaintCircuitTripped(): boolean {
  return false
}

interface ClassColor {
  /** RegExp match against the IFC class name (without the leading
   *  Ifc, but case-insensitive). Order matters: first match wins. */
  match: RegExp
  /** CSS hex string. */
  color: string
}

const CLASS_PALETTE: ClassColor[] = [
  { match: /^IfcDuct/i, color: '#fbbf24' },
  { match: /^IfcAirTerminal/i, color: '#fde68a' },
  { match: /^IfcFlowFitting/i, color: '#facc15' },
  { match: /^IfcFlowSegment/i, color: '#fb923c' },
  { match: /^IfcFlowController/i, color: '#dc2626' },
  { match: /^IfcPipe/i, color: '#fb923c' },
  { match: /^IfcSanitaryTerminal/i, color: '#7dd3fc' },
  { match: /^IfcValve/i, color: '#dc2626' },
  { match: /^IfcFlowMeter/i, color: '#dc2626' },
  { match: /^IfcCable/i, color: '#ef4444' },
  { match: /^IfcElectric/i, color: '#f87171' },
  { match: /^IfcLightFixture/i, color: '#fef3c7' },
  { match: /^IfcBeam/i, color: '#b08968' },
  { match: /^IfcColumn/i, color: '#a07650' },
  { match: /^IfcMember/i, color: '#b08968' },
  { match: /^IfcPlate/i, color: '#9ca3af' },
  { match: /^IfcFooting/i, color: '#7c6f64' },
  { match: /^IfcPile/i, color: '#7c6f64' },
  { match: /^IfcReinforcing/i, color: '#52525b' },
  { match: /^IfcWall/i, color: '#f5f0e6' },
  { match: /^IfcCurtainWall/i, color: '#a5b4fc' },
  { match: /^IfcSlab/i, color: '#6b7280' },
  { match: /^IfcRoof/i, color: '#7f5539' },
  { match: /^IfcCovering/i, color: '#8b6f47' },
  { match: /^IfcStair/i, color: '#a8a29e' },
  { match: /^IfcRamp/i, color: '#a8a29e' },
  { match: /^IfcDoor/i, color: '#7c2d12' },
  { match: /^IfcWindow/i, color: '#ea580c' },
  { match: /^IfcOpeningElement/i, color: '#71717a' },
  { match: /^IfcFurnishingElement/i, color: '#a8a29e' },
  { match: /^IfcFurniture/i, color: '#a8a29e' },
  { match: /^IfcSystemFurnitureElement/i, color: '#a8a29e' },
  { match: /^IfcSite/i, color: '#84cc16' },
  { match: /^IfcGeographicElement/i, color: '#a3e635' },
  { match: /^IfcSpace/i, color: '#bbf7d0' },
  { match: /^IfcBuildingElementProxy/i, color: '#cbd5e1' },
  { match: /^IfcDiscreteAccessory/i, color: '#94a3b8' },
  { match: /^IfcMechanicalFastener/i, color: '#71717a' },
]

const LIGHT_BG_PALETTE: ClassColor[] = [
  { match: /^IfcDuct/i, color: '#d97706' },
  { match: /^IfcAirTerminal/i, color: '#b45309' },
  { match: /^IfcFlowFitting/i, color: '#ca8a04' },
  { match: /^IfcFlowSegment/i, color: '#c2410c' },
  { match: /^IfcFlowController/i, color: '#991b1b' },
  { match: /^IfcPipe/i, color: '#c2410c' },
  { match: /^IfcSanitaryTerminal/i, color: '#0369a1' },
  { match: /^IfcValve/i, color: '#991b1b' },
  { match: /^IfcFlowMeter/i, color: '#991b1b' },
  { match: /^IfcCable/i, color: '#b91c1c' },
  { match: /^IfcElectric/i, color: '#dc2626' },
  { match: /^IfcLightFixture/i, color: '#a16207' },
  { match: /^IfcBeam/i, color: '#7c5e3c' },
  { match: /^IfcColumn/i, color: '#6b4f2e' },
  { match: /^IfcMember/i, color: '#7c5e3c' },
  { match: /^IfcPlate/i, color: '#52525b' },
  { match: /^IfcFooting/i, color: '#3f3f46' },
  { match: /^IfcPile/i, color: '#3f3f46' },
  { match: /^IfcReinforcing/i, color: '#27272a' },
  { match: /^IfcWall/i, color: '#3f3f46' },
  { match: /^IfcCurtainWall/i, color: '#4338ca' },
  { match: /^IfcSlab/i, color: '#27272a' },
  { match: /^IfcRoof/i, color: '#5d3416' },
  { match: /^IfcCovering/i, color: '#6b4226' },
  { match: /^IfcStair/i, color: '#52525b' },
  { match: /^IfcRamp/i, color: '#52525b' },
  { match: /^IfcDoor/i, color: '#451a03' },
  { match: /^IfcWindow/i, color: '#9a3412' },
  { match: /^IfcOpeningElement/i, color: '#52525b' },
  { match: /^IfcFurnishingElement/i, color: '#525252' },
  { match: /^IfcFurniture/i, color: '#525252' },
  { match: /^IfcSystemFurnitureElement/i, color: '#525252' },
  { match: /^IfcSite/i, color: '#3f6212' },
  { match: /^IfcGeographicElement/i, color: '#4d7c0f' },
  { match: /^IfcSpace/i, color: '#15803d' },
  { match: /^IfcBuildingElementProxy/i, color: '#475569' },
  { match: /^IfcDiscreteAccessory/i, color: '#475569' },
  { match: /^IfcMechanicalFastener/i, color: '#3f3f46' },
]

function pickClassColor(category: string, palette: ClassColor[]): string | null {
  for (const entry of palette) {
    if (entry.match.test(category)) return entry.color
  }
  return null
}

/** Cor (hex CSS) da paleta por classe para uma categoria IFC, ou null
 *  se a categoria não estiver na paleta. Fonte única partilhada entre o
 *  modo "cores por classe" do viewer e o export Google Earth (KMZ), para
 *  a mesma coerência visual nos dois sítios. `bg` escolhe a variante
 *  (fundo claro usa tons mais saturados). */
export function classColorForCategory(
  category: string,
  bg: 'dark' | 'light' = 'dark',
): string | null {
  const palette = bg === 'light' ? LIGHT_BG_PALETTE : CLASS_PALETTE
  return pickClassColor(category, palette)
}

// ----------------------------------------------------------------------------
// Per-criterion classifier construction.
//
// Each classifier returns Map<localId, bucketKey> for one model. Items
// missing from the map are unbucketed (rendered with whatever sits
// underneath: palette colour, or model native material).
// ----------------------------------------------------------------------------

const PROPERTY_SAMPLE_LIMIT = 10000

/** Single source of truth for "every item with geometry, grouped by IFC
 *  category". Cached per modelId. Powers the entity classifier AND the
 *  palette layer; on tile-spawn refresh the cached value is reused so we
 *  don't pay a worker round-trip per tile.
 *
 *  Filters out non-geometric items (IfcPropertySet, IfcMaterial,
 *  IfcQuantityArea, etc.) so the legend in the Colorir por… panel only
 *  lists categories that have something to colour. */
async function getItemsByCategory(
  model: FragmentsModel,
): Promise<Record<string, number[]>> {
  const cached = itemCategoryCache.get(model.modelId)
  if (cached) return cached
  let raw: Record<string, number[]> = {}
  let geometric = new Set<number>()
  try {
    raw = await model.getItemsOfCategories([/.*/])
  } catch (err) {
    console.warn('[paint] getItemsOfCategories failed', err)
  }
  try {
    const ids = await model.getItemsIdsWithGeometry()
    geometric = new Set(ids)
  } catch (err) {
    console.warn(
      '[paint] getItemsIdsWithGeometry failed; not filtering non-geometric items',
      err,
    )
  }
  const filtered: Record<string, number[]> = {}
  for (const [cat, ids] of Object.entries(raw)) {
    if (!ids || ids.length === 0) continue
    const onlyGeo =
      geometric.size > 0 ? ids.filter((id) => geometric.has(id)) : ids
    if (onlyGeo.length > 0) filtered[cat] = onlyGeo
  }
  itemCategoryCache.set(model.modelId, filtered)
  return filtered
}

async function classifyByEntity(
  model: FragmentsModel,
): Promise<Map<number, string>> {
  const out = new Map<number, string>()
  const bucket = await getItemsByCategory(model)
  for (const [cat, ids] of Object.entries(bucket)) {
    if (!ids) continue
    for (const id of ids) out.set(id, cat)
  }
  return out
}

async function classifyBySystem(
  model: FragmentsModel,
): Promise<Map<number, string>> {
  const out = new Map<number, string>()
  if (!rawIfc.hasModel(model.modelId)) return out
  const groupCounts = rawIfc.getGroupMemberCounts(model.modelId)
  for (const [groupId] of groupCounts) {
    const ids = rawIfc.getGroupMembers(model.modelId, groupId)
    if (ids.length === 0) continue
    const attrs = rawIfc.getAttributes(model.modelId, groupId)
    const name = attrs?.name ?? `#${groupId}`
    for (const mid of ids) out.set(mid, name)
  }
  return out
}

/** Read getItemsData for every geometric item, tolerating large models
 *  by capping at PROPERTY_SAMPLE_LIMIT and pulling attributes only
 *  (relations are pulled when needed by the inner extractor, driven
 *  by the wantsRelations flag). Returns paired ids+items so the caller
 *  can extract its bucket key. */
async function loadItemsForExtraction(
  model: FragmentsModel,
  wantsRelations: boolean,
): Promise<{ ids: number[]; items: unknown[]; truncated: boolean }> {
  const bucket = await getItemsByCategory(model)
  const allIds: number[] = []
  for (const ids of Object.values(bucket)) {
    if (ids) allIds.push(...ids)
  }
  const truncated = allIds.length > PROPERTY_SAMPLE_LIMIT
  const ids = truncated ? allIds.slice(0, PROPERTY_SAMPLE_LIMIT) : allIds
  let items: unknown[]
  try {
    items = await model.getItemsData(ids, {
      attributesDefault: true,
      relationsDefault: { attributes: true, relations: wantsRelations },
    })
  } catch (err) {
    console.warn('[paint] loadItemsForExtraction: getItemsData failed', err)
    return { ids: [], items: [], truncated }
  }
  return { ids, items, truncated }
}

function readAttrValue(item: unknown, name: string): string | null {
  if (!item || typeof item !== 'object') return null
  const v = (item as Record<string, unknown>)[name]
  if (v == null) return null
  if (typeof v === 'object' && 'value' in (v as object)) {
    const raw = (v as { value: unknown }).value
    if (raw == null || raw === '') return null
    return String(raw)
  }
  if (v === '') return null
  return String(v)
}

async function classifyByPredefinedType(
  model: FragmentsModel,
): Promise<Map<number, string>> {
  const out = new Map<number, string>()
  const { ids, items } = await loadItemsForExtraction(model, false)
  ids.forEach((id, idx) => {
    const item = items[idx]
    const value =
      readAttrValue(item, 'PredefinedType') ??
      readAttrValue(item, 'ObjectType')
    if (value && value !== 'NOTDEFINED' && value !== 'USERDEFINED') {
      out.set(id, value)
    }
  })
  return out
}

async function classifyByType(
  model: FragmentsModel,
): Promise<Map<number, string>> {
  // IfcRelDefinesByType is exposed via the IsTypedBy inverse on items.
  // The rel object's RelatingType is the IfcTypeObject (eg. IfcWallType).
  // We bucket by the type's Name.
  const out = new Map<number, string>()
  const { ids, items } = await loadItemsForExtraction(model, true)
  ids.forEach((id, idx) => {
    const item = items[idx] as Record<string, unknown> | null
    if (!item) return
    const rels = item.IsTypedBy as unknown[] | undefined
    if (!Array.isArray(rels) || rels.length === 0) return
    for (const r of rels) {
      if (!r || typeof r !== 'object') continue
      const rt = (r as Record<string, unknown>).RelatingType
      const name =
        rt && typeof rt === 'object' && 'Name' in (rt as object)
          ? readAttrValue(rt, 'Name')
          : null
      if (name) {
        out.set(id, name)
        break
      }
    }
  })
  return out
}

async function classifyByMaterial(
  model: FragmentsModel,
): Promise<Map<number, string>> {
  // IfcRelAssociatesMaterial sits in HasAssociations on the item.
  // RelatingMaterial may be IfcMaterial (.Name), IfcMaterialList
  // (.Materials[].Name), IfcMaterialLayerSetUsage (...layered),
  // IfcMaterialConstituentSet (...named constituents). We extract a
  // best-effort label, joining names with " / " when multiple materials
  // are present.
  const out = new Map<number, string>()
  const { ids, items } = await loadItemsForExtraction(model, true)
  ids.forEach((id, idx) => {
    const item = items[idx] as Record<string, unknown> | null
    if (!item) return
    const rels = item.HasAssociations as unknown[] | undefined
    if (!Array.isArray(rels) || rels.length === 0) return
    const label = materialDasAssociacoes(rels)
    if (label) out.set(id, label)
  })
  return out
}

/** O material nas associações de um elemento.
 *
 *  O Fragments achata a relação: o `HasAssociations` de um elemento
 *  aponta DIRECTAMENTE para o IfcMaterial (ou a lista, ou o conjunto de
 *  camadas), sem o IfcRelAssociatesMaterial pelo meio. O código só
 *  procurava `RelatingMaterial`, e no modelo de exemplo os 85 pilares e
 *  vigas saíam sem material. Aceitam-se as duas formas. */
function materialDasAssociacoes(rels: unknown[]): string | null {
  for (const r of rels) {
    if (!r || typeof r !== 'object') continue
    const ro = r as Record<string, unknown>
    const categoria = String(
      readAttrValue(ro, '_category') ?? ro.type ?? ro.expressType ?? '',
    ).toUpperCase()
    const target =
      ro.RelatingMaterial ??
      (categoria.startsWith('IFCMATERIAL') ? ro : null)
    if (!target || typeof target !== 'object') continue
    const label = extractMaterialLabel(target)
    if (label) return label
  }
  return null
}

/** O material de cada um dos elementos pedidos, pelo mesmo caminho que o
 *  "Colorir por material" usa. Para a comparação com a nuvem de pontos
 *  poder escolher "só o aço" sem ler os dados do modelo inteiro. */
export async function materiaisDosElementos(
  model: FragmentsModel,
  ids: number[],
): Promise<Map<number, string>> {
  const out = new Map<number, string>()
  if (ids.length === 0) return out
  let items: unknown[]
  try {
    items = await model.getItemsData(ids, {
      attributesDefault: true,
      relationsDefault: { attributes: true, relations: true },
    })
  } catch (err) {
    console.warn('[paint] materiaisDosElementos: getItemsData failed', err)
    return out
  }
  ids.forEach((id, idx) => {
    const item = items[idx] as Record<string, unknown> | null
    const rels = item?.HasAssociations as unknown[] | undefined
    if (!Array.isArray(rels)) return
    const label = materialDasAssociacoes(rels)
    if (label) out.set(id, label)
  })
  return out
}

function extractMaterialLabel(target: unknown): string | null {
  if (!target || typeof target !== 'object') return null
  const t = target as Record<string, unknown>
  // IfcMaterial: has Name.
  const directName = readAttrValue(t, 'Name')
  if (directName) return directName
  // IfcMaterialList: Materials[]
  const matList = t.Materials as unknown[] | undefined
  if (Array.isArray(matList) && matList.length > 0) {
    const names: string[] = []
    for (const m of matList) {
      const n = readAttrValue(m, 'Name')
      if (n) names.push(n)
    }
    if (names.length > 0) return names.join(' / ')
  }
  // IfcMaterialLayerSetUsage → ForLayerSet → MaterialLayers[] → Material.Name
  const lsUsage = t.ForLayerSet as Record<string, unknown> | undefined
  const layerSet = lsUsage ?? (t.MaterialLayers ? t : undefined)
  if (layerSet) {
    const layers = (layerSet.MaterialLayers ?? layerSet.Layers) as unknown[] | undefined
    if (Array.isArray(layers)) {
      const names: string[] = []
      for (const l of layers) {
        if (!l || typeof l !== 'object') continue
        const mat = (l as Record<string, unknown>).Material
        const n = readAttrValue(mat, 'Name')
        if (n) names.push(n)
      }
      if (names.length > 0) return names.join(' / ')
    }
  }
  // IfcMaterialConstituentSet → MaterialConstituents[] → Material.Name
  const constituents = t.MaterialConstituents as unknown[] | undefined
  if (Array.isArray(constituents) && constituents.length > 0) {
    const names: string[] = []
    for (const c of constituents) {
      if (!c || typeof c !== 'object') continue
      const mat = (c as Record<string, unknown>).Material
      const n = readAttrValue(mat, 'Name')
      if (n) names.push(n)
    }
    if (names.length > 0) return names.join(' / ')
  }
  // IfcMaterialProfileSet → MaterialProfiles[] → Material.Name
  const profiles = t.MaterialProfiles as unknown[] | undefined
  if (Array.isArray(profiles) && profiles.length > 0) {
    const names: string[] = []
    for (const p of profiles) {
      if (!p || typeof p !== 'object') continue
      const mat = (p as Record<string, unknown>).Material
      const n = readAttrValue(mat, 'Name')
      if (n) names.push(n)
    }
    if (names.length > 0) return names.join(' / ')
  }
  return null
}

async function classifyByProperty(
  model: FragmentsModel,
  key: string,
): Promise<Map<number, string>> {
  const out = new Map<number, string>()
  const target = key.trim()
  if (!target) return out
  const dotIdx = target.indexOf('.')
  const psetName = dotIdx >= 0 ? target.slice(0, dotIdx) : null
  const propName = dotIdx >= 0 ? target.slice(dotIdx + 1) : target
  const wantsRelations = psetName !== null
  const { ids, items } = await loadItemsForExtraction(model, wantsRelations)
  ids.forEach((id, idx) => {
    const item = items[idx] as Record<string, unknown> | null
    if (!item) return
    let value: string | null = null
    if (psetName) {
      const rels = item.IsDefinedBy as unknown[] | undefined
      if (Array.isArray(rels)) {
        for (const r of rels) {
          if (!r || typeof r !== 'object') continue
          const ro = r as Record<string, unknown>
          const nm = readAttrValue(ro, 'Name')
          if (nm !== psetName) continue
          const props = ro.HasProperties as unknown[] | undefined
          if (!Array.isArray(props)) continue
          for (const p of props) {
            if (!p || typeof p !== 'object') continue
            const po = p as Record<string, unknown>
            if (readAttrValue(po, 'Name') !== propName) continue
            const nv = po.NominalValue
            if (nv && typeof nv === 'object' && 'value' in (nv as object)) {
              const raw = (nv as { value: unknown }).value
              if (raw != null && raw !== '') value = String(raw)
            }
          }
        }
      }
    } else {
      value = readAttrValue(item, propName)
    }
    if (value !== null) out.set(id, value)
  })
  return out
}

async function classifierForCriterion(
  model: FragmentsModel,
  criterion: ColorByCriterion,
): Promise<Map<number, string>> {
  switch (criterion.mode) {
    case 'entity':
      return classifyByEntity(model)
    case 'predefinedType':
      return classifyByPredefinedType(model)
    case 'system':
      return classifyBySystem(model)
    case 'material':
      return classifyByMaterial(model)
    case 'type':
      return classifyByType(model)
    case 'property':
      return classifyByProperty(model, criterion.propertyKey ?? '')
  }
}

// ----------------------------------------------------------------------------
// Federation: build the unified legend across all loaded models so the
// same bucket key gets the same colour everywhere. We compute keys
// once per "Aplicar" click (cached against the criterion identity)
// and then reuse them on every onViewUpdated repaint to avoid
// re-fetching getItemsData on tile spawns.
// ----------------------------------------------------------------------------

interface LegendCacheEntry {
  criterion: ColorByCriterion
  bucketByModel: Map<string, Map<number, string>>
  legend: ColorByBucket[]
}

let legendCache: LegendCacheEntry | null = null

export interface ColorByBucket {
  key: string
  color: string
  count: number
}

const GOLDEN_ANGLE = 137.508

function colourForBucket(index: number): string {
  const hue = (index * GOLDEN_ANGLE) % 360
  return `hsl(${hue.toFixed(1)}, 65%, 55%)`
}

function criterionEquals(
  a: ColorByCriterion | null,
  b: ColorByCriterion | null,
): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.mode === b.mode && (a.propertyKey ?? '') === (b.propertyKey ?? '')
}

/** (Re-)classify every loaded model against the criterion and assign
 *  a global colour per distinct bucket key. Caches the result so
 *  later repaints (tile spawns) don't re-fetch getItemsData. */
async function buildLegend(
  criterion: ColorByCriterion,
): Promise<LegendCacheEntry> {
  const bucketByModel = new Map<string, Map<number, string>>()
  const counts = new Map<string, number>()
  for (const [modelId, model] of viewerApi.iterModels()) {
    const map = await classifierForCriterion(model, criterion)
    bucketByModel.set(modelId, map)
    for (const v of map.values()) {
      counts.set(v, (counts.get(v) ?? 0) + 1)
    }
  }
  const keys = Array.from(counts.keys()).sort()
  const legend: ColorByBucket[] = keys.map((key, i) => ({
    key,
    color: colourForBucket(i),
    count: counts.get(key) ?? 0,
  }))
  return { criterion, bucketByModel, legend }
}

/** Single inflight guard so two parallel repaintModel calls don't both
 *  rebuild the legend. */
let legendBuildInflight: Promise<LegendCacheEntry> | null = null

/** Return a legendCache that covers ALL currently-loaded models for
 *  the requested criterion. Rebuilds when the criterion changes OR
 *  when a newly-federated model isn't covered yet. */
async function ensureLegend(
  criterion: ColorByCriterion,
): Promise<LegendCacheEntry> {
  if (legendCache && criterionEquals(legendCache.criterion, criterion)) {
    let covered = true
    for (const [modelId] of viewerApi.iterModels()) {
      if (!legendCache.bucketByModel.has(modelId)) {
        covered = false
        break
      }
    }
    if (covered) return legendCache
  }
  if (legendBuildInflight) return legendBuildInflight
  const p = (async () => {
    const built = await buildLegend(criterion)
    legendCache = built
    return built
  })()
  legendBuildInflight = p
  try {
    return await p
  } finally {
    if (legendBuildInflight === p) legendBuildInflight = null
  }
}

// ----------------------------------------------------------------------------
// Repaint: the single place that talks to model.setColor / resetColor.
// All other public functions call into this.
//
// Pass order:
//   0. (only when reset===true) resetColor(undefined) frees the
//      worker's bitmask slots. Mode-state changes pass reset=true;
//      tile-spawn refreshes pass reset=false (cheaper, and avoids
//      the race that wipes good colours during orbit).
//   1. Apply per-class palette (if classColorsEnabled).
//   2. Apply colorBy criterion (if colorByActive).
//   3. Apply manual paints (Aparência panel).
//
// Re-applying the same (id, colour) tuples does NOT grow the bitmask,
// so tile-spawn refreshes are leak-free.
// ----------------------------------------------------------------------------

/** Serialize repaints per model. Concurrent callers chain onto the
 *  prior promise so we never have two resetColor / setColor passes
 *  running in parallel for the same model. */
async function repaintModel(
  model: FragmentsModel,
  opts: { reset?: boolean } = {},
): Promise<void> {
  if (!model) return
  const key = model.modelId
  const prior = repaintInflight.get(key)
  const work = (async () => {
    if (prior) await prior.catch(() => {})
    await doRepaintOnce(model, opts.reset === true)
  })()
  repaintInflight.set(key, work)
  try {
    await work
  } finally {
    if (repaintInflight.get(key) === work) repaintInflight.delete(key)
  }
}

async function doRepaintOnce(
  model: FragmentsModel,
  reset: boolean,
): Promise<void> {
  // Build the resolved (localId → hex) map by composing the three layers
  // in order. Later layers overwrite earlier ones: manual > criterion >
  // palette, so manual paints always win and criterion overrides palette.
  // We then push the whole map to the paintEngine in a single texture
  // update. Zero Fragments setColor calls; bitmask never grows.
  const state = useAppStore.getState()
  const merged = new Map<number, string>()

  // Layer 1: per-class palette
  if (state.classColorsEnabled) {
    const palette =
      (state.bgMode ?? 'dark') === 'light' ? LIGHT_BG_PALETTE : CLASS_PALETTE
    const bucket = await getItemsByCategory(model)
    for (const [category, ids] of Object.entries(bucket)) {
      if (!ids || ids.length === 0) continue
      const colour = pickClassColor(category, palette)
      if (!colour) continue
      for (const id of ids) merged.set(id, colour)
    }
  }

  // Layer 2: ColorBy criterion (legend covers every loaded model)
  const active = state.colorByActive
  if (active) {
    const cache = await ensureLegend(active)
    const map = cache.bucketByModel.get(model.modelId)
    if (map && map.size > 0) {
      const colourByKey = new Map<string, string>()
      for (const b of cache.legend) colourByKey.set(b.key, b.color)
      for (const [lid, key] of map) {
        const hex = colourByKey.get(key)
        if (hex) merged.set(lid, hex)
      }
    }
  }

  // Layer 3: manual paints (Aparência panel)
  const reg = manualPaints.get(model.modelId)
  if (reg && reg.size > 0) {
    for (const [lid, hex] of reg) merged.set(lid, hex)
  }

  // Layer 4: diff overlay (Comparar IFC): top layer, always wins so the
  // comparison reads clearly over any palette / criterion / manual paint.
  const dreg = diffPaints.get(model.modelId)
  if (dreg && dreg.size > 0) {
    for (const [lid, hex] of dreg) merged.set(lid, hex)
  }

  // Push the resolved palette to the paint texture. When `reset === true`
  // we wipe everything first so removed items go back to default.
  if (reset || merged.size > 0) {
    paintEngine.clearAllItemColors(model.modelId)
  }
  if (merged.size > 0) {
    // KEY: the geometry's per-vertex `id` attribute stores Fragments'
    // internal "itemIndex" (sample.item() in the worker), NOT the
    // localId the public API exposes. We translate localId → itemIndex
    // via the worker's getItemIdsByLocalIds method so the texture lookup
    // hits the correct slot. Without this, items render with the colour
    // of whichever item happens to live at texture pixel (localId mod
    // textureSide, localId / textureSide), i.e. completely wrong.
    const localIds = Array.from(merged.keys())
    let itemIndices: number[]
    try {
      itemIndices = (await (
        model as unknown as {
          threads: {
            invoke: (
              modelId: string,
              method: string,
              args: unknown[],
            ) => Promise<number[]>
          }
        }
      ).threads.invoke(model.modelId, 'getItemIdsByLocalIds', [
        localIds,
      ])) as number[]
    } catch (err) {
      console.warn(
        '[classColors] getItemIdsByLocalIds failed; falling back to localId-keyed paint (may render wrong colours)',
        err,
      )
      itemIndices = localIds
    }
    const translated = new Map<number, string>()
    for (let i = 0; i < localIds.length; i++) {
      const idx = itemIndices[i]
      if (idx === undefined || idx < 0) continue
      const hex = merged.get(localIds[i])
      if (hex) translated.set(idx, hex)
    }
    paintEngine.setItemColors(model.modelId, translated)
  }
  // Make sure the paint engine is wired to this model (idempotent, no-op
  // after the first call, but covers the case where repaintModel is the
  // first thing that ever touches the model).
  paintEngine.attachPaintToModel(model)

  viewerApi._invalidate()
  void reset // legacy param; kept for API compatibility but no longer used
}

// ============================================================================
// Public API
// ============================================================================

/** Repaint every loaded model. Default: reset the bitmask first.
 *  Each setColor call on Fragments 3.4 consumes HighlightHelper slots
 *  even for tuples already in the table, so re-applying without
 *  resetting is NOT leak-free: the bitmask grows on every pass and
 *  trips "Memory overflow!" within a few orbits. Reset is therefore
 *  the safe default; pass { reset: false } only when a caller
 *  knows the bitmask is already in a good state. */
export async function repaintAllModels(
  opts: { reset?: boolean } = {},
): Promise<void> {
  const reset = opts.reset !== false
  await Promise.all(
    Array.from(viewerApi.iterModels()).map(([, model]) =>
      repaintModel(model, { reset }),
    ),
  )
}

/** Repaint a single model: the hook for onViewUpdated and per-model
 *  refreshes. Default: reset (same rationale as repaintAllModels). */
export async function repaintOneModel(
  model: FragmentsModel,
  opts: { reset?: boolean } = {},
): Promise<void> {
  const reset = opts.reset !== false
  await repaintModel(model, { reset })
}

/** Apply a colour-by criterion. Builds the legend across every loaded
 *  model so the same bucket key gets the same colour everywhere, then
 *  repaints. Returns the legend so the caller can show it.  */
export async function applyColorByCriterion(
  criterion: ColorByCriterion,
): Promise<{ legend: ColorByBucket[] }> {
  legendCache = null
  resetAllCircuits()
  useAppStore.getState().setColorByActive(criterion)
  const cache = await ensureLegend(criterion)
  await repaintAllModels({ reset: true })
  return { legend: cache.legend }
}

/** Os elementos de um grupo da legenda, em todos os modelos. Serve o
 *  gráfico do painel: clicar numa barra selecciona esses elementos.
 *  Vazio quando não há critério aplicado. */
export function itensDoGrupo(chave: string): { modelId: string; localId: number }[] {
  if (!legendCache) return []
  const out: { modelId: string; localId: number }[] = []
  for (const [modelId, mapa] of legendCache.bucketByModel) {
    for (const [localId, k] of mapa) if (k === chave) out.push({ modelId, localId })
  }
  return out
}

/** Drop the colour-by criterion (palette + manual paints stay). */
export async function clearColorByCriterion(): Promise<void> {
  legendCache = null
  resetAllCircuits()
  useAppStore.getState().setColorByActive(null)
  await repaintAllModels({ reset: true })
}

/** Drop colour-by criterion AND every manual paint.  Palette stays
 *  if its toggle is on. */
export async function clearAllPaints(model?: FragmentsModel): Promise<void> {
  legendCache = null
  manualPaints.clear()
  resetAllCircuits()
  useAppStore.getState().setColorByActive(null)
  if (model) {
    await repaintModel(model, { reset: true })
  } else {
    await repaintAllModels({ reset: true })
  }
}

/** Manual paint: a one-shot per-element colour applied as the top
 *  layer. Stays through criterion changes; cleared by clearAllPaints
 *  or resetCustomColor. */
export async function paintCustomColor(
  model: FragmentsModel,
  localIds: number[],
  hex: string,
): Promise<void> {
  if (!model || localIds.length === 0) return
  resetCircuitForModel(model.modelId)
  let reg = manualPaints.get(model.modelId)
  if (!reg) {
    reg = new Map()
    manualPaints.set(model.modelId, reg)
  }
  for (const id of localIds) reg.set(id, hex)
  await repaintModel(model, { reset: true })
}

/** Diff overlay: replace the whole diff layer with the given per-model
 *  (localId → hex) maps and repaint. Driven by the Comparar IFC panel
 *  (removed=red on A, added=green / modified=amber on B). Repaints every
 *  model with reset so a previous diff on other models is cleared too. */
export async function setDiffColors(
  byModel: Map<string, Map<number, string>>,
): Promise<void> {
  diffPaints.clear()
  for (const [modelId, map] of byModel) {
    if (map.size > 0) diffPaints.set(modelId, new Map(map))
  }
  await repaintAllModels({ reset: true })
}

/** Drop the diff overlay (other layers stay). No-op when nothing is set. */
export async function clearDiffColors(): Promise<void> {
  if (diffPaints.size === 0) return
  diffPaints.clear()
  await repaintAllModels({ reset: true })
}

/** Strip manual paint from the given items. Other layers stay. */
export async function resetCustomColor(
  model: FragmentsModel,
  localIds: number[],
): Promise<void> {
  if (!model || localIds.length === 0) return
  const reg = manualPaints.get(model.modelId)
  if (reg) {
    for (const id of localIds) reg.delete(id)
    if (reg.size === 0) manualPaints.delete(model.modelId)
  }
  await repaintModel(model, { reset: true })
}

// ----------------------------------------------------------------------------
// Compatibility shims for existing call sites. They route through
// repaintModel so the old toggles keep working with the new engine.
// ----------------------------------------------------------------------------

/** Legacy entry point kept for the per-class palette toggle code paths
 *  in the ribbon, Diagnostics popover, and bg-mode switcher. The
 *  effective behaviour is "repaint with the current store state".
 *  Reset the bitmask because the palette layer's colours are about
 *  to change. */
export async function applyClassColorPalette(
  model: FragmentsModel,
  _options?: { bg?: 'dark' | 'light' },
): Promise<number> {
  await repaintModel(model, { reset: true })
  return 0
}

/** Legacy entry point kept for the per-class palette toggle when the
 *  flag is being switched off. resetColor + repaint covers it. */
export async function clearClassColorPalette(
  model: FragmentsModel,
): Promise<void> {
  await repaintModel(model, { reset: true })
}

/** Drop cached state for a model that's about to be unloaded. Called
 *  from viewerApi.removeModel so a re-loaded model with a recycled
 *  modelId doesn't see stale category data. */
export function forgetModel(modelId: string): void {
  itemCategoryCache.delete(modelId)
  manualPaints.delete(modelId)
  diffPaints.delete(modelId)
  resetCircuitForModel(modelId)
  if (legendCache) legendCache.bucketByModel.delete(modelId)
}
